import React from "react";
import {
  GraduationCap,
  Plus,
  Trash,
  CaretDown,
  CaretUp,
  Warning,
  Check,
  PencilSimple,
} from "@phosphor-icons/react";
import { Label } from "../../../components/ui/Input";

/** Factory: empty manually-added Ausbildungs-row. */
export function EmptyAusbildung() {
  return {
    label: "",
    months: 0,
    anrechenbar: true,
    begruendung: "",
    ai_matched: false,
    bypass_cap: false,
  };
}

/** Format months → "X J." or "X M." (years preferred when divisible). */
function formatMonths(m) {
  const num = Number(m) || 0;
  if (num <= 0) return "0 M.";
  const yearsPart = num / 12;
  if (Math.abs(yearsPart - Math.round(yearsPart)) < 0.05) {
    const y = Math.round(yearsPart);
    return y === 1 ? "1 J." : `${y} J.`;
  }
  return `${Math.round(num)} M.`;
}

/**
 * Iter 20.36.f14.17 — AusbildungRow im UI-Sprach-Standard von VordienstRow.
 *
 * Kompakter einzeiliger Header + Aufklapp-Chevron (Details auf Klick). Alle
 * Rows starten collapsed (auch neu hinzugefügte); das schafft eine ruhige
 * Übersicht wie im Vordienst-Block.
 *
 * Prüf-Signal-Logik analog `_review_verified` bei Vordienstzeiten:
 *  - `_needs_proof=true` UND `_review_verified≠true`  → 3-px amber Left-Border
 *    + amber Warning-Icon-Button im Header (Klick → toggelt `_review_verified`)
 *  - Sobald der Sachbearbeiter das Icon abhakt, wird das Icon grün (Check)
 *    und der Amber-Streifen verschwindet — die Zeile wirkt „aufgeräumt".
 *  - Filter-Chip „X Ausbildungen prüfen" (oben) filtert alle mit
 *    `_needs_proof && !_review_verified`.
 *
 * Zusätzlich der bestehende Klick-Confirm-Badge „Nachweis fehlt — jetzt
 * bestätigen" (Iter 20.36.f14.16) im expanded-State: derselbe Effekt wie
 * das Warning-Icon (Header), aber verbal + textuell, weil er im ausge-
 * klappten Panel sichtbar ist und für User verständlicher formuliert.
 */
function AusbildungRow({ row, regeln, index, onChange, onRemove }) {
  // Iter 20.36.f14.17 — Rows starten IMMER collapsed (auch bei bestehender
  // Begründung — konsistent mit VordienstRow-Verhalten).
  const [open, setOpen] = React.useState(false);

  const ruleLabels = React.useMemo(
    () => (regeln || []).map((r) => r.label),
    [regeln],
  );
  const currentLabelInRules = ruleLabels.includes(row.label);
  const initialManualMode = Boolean(row.label && !currentLabelInRules);
  const [manualMode, setManualMode] = React.useState(initialManualMode);

  const needsProof = row._needs_proof === true;
  const reviewVerified = row._review_verified === true;
  // Iter 20.36.f14.19 — Rückkehr zur Iter-19.63.64-Vereinbarung:
  // „Weak Evidence" (z.B. SV-Auszug nennt „Angestelltenlehrling", aber
  // kein Lehrabschluss-Zeugnis im Corpus) → Row IST sichtbar, aber mit
  // Amber-Signal + AUS-Toggle + Warning-Icon-Button. Beim F14.17-UI-
  // Refactor hatte ich versehentlich nur `_needs_proof` als Signal-Quelle
  // gekoppelt und den `evidence`-Weg gebrochen. Jetzt beide Wege wieder
  // aktiv: Row braucht sichtbare Prüfung wenn EINER von beiden feuert.
  const weakEvidenceUnverified = row.evidence === "weak" && !reviewVerified;
  const showAmberSignal = (needsProof || weakEvidenceUnverified) && !reviewVerified;

  const onSelectChange = (newLabel) => {
    const rule = (regeln || []).find((r) => r.label === newLabel);
    // Iter 20.36.f14.22 — Auto-Begründung erkennen wir jetzt am `ai_matched`-
    // Flag statt am „Auto-Vorschlag"-Text-Prefix (der wurde in dieser Iter
    // durch sprechendere Formulierungen ersetzt). Fallback-Prefixe bleiben
    // für Kompatibilität mit älteren State-Snapshots aktiv.
    const oldBegr = (row.begruendung || "").trim();
    const wasAutoBegr =
      row.ai_matched === true ||
      oldBegr.startsWith("Auto-Vorschlag:") ||
      oldBegr.startsWith("Diese Zeit wird gemäß KV-Sonderregel");
    const willBeBypass = rule ? Boolean(rule.bypass_cap) : false;
    let newBegr = wasAutoBegr ? "" : row.begruendung;
    if (willBeBypass && (!newBegr || newBegr.trim() === "")) {
      newBegr =
        "Diese KV-Sonderregel wird IMMER zusätzlich zu den anrechenbaren " +
        "Vordienstzeiten angerechnet — unabhängig von der Höchstgrenze. Greift also " +
        "auch dann, wenn die anderen Zeiten die Höchstgrenze noch nicht erreichen. " +
        "Bitte prüfen, ob im konkreten Fall einschlägig (Tätigkeits" +
        "abhängigkeit beachten).";
    }
    onChange({
      ...row,
      label: newLabel,
      months: rule ? rule.months : row.months,
      bypass_cap: willBeBypass,
      ai_matched: false,
      begruendung: newBegr,
    });
  };

  const enterManualMode = () => {
    setManualMode(true);
    onChange({ ...row, label: "", months: 0, bypass_cap: false, ai_matched: false });
  };

  const exitManualMode = () => {
    setManualMode(false);
    if (row.label && !ruleLabels.includes(row.label)) {
      onChange({ ...row, label: "", months: 0, ai_matched: false });
    }
  };

  // Confirm-Nachweis: sowohl vom Warning-Icon (Header) als auch vom
  // Text-Badge (expanded) aufrufbar. Setzt evidence auf strong, damit
  // die Zeile visuell entspannt. Iter 20.36.f14.19 — Bei weak-evidence-
  // Bestätigung wird ZUSÄTZLICH der `anrechenbar`-Toggle auf true gestellt
  // (analog zu Iter 19.63.64: User bestätigt „Zeugnis liegt vor" → Regel
  // greift). Bei reinem `_needs_proof`-Fall bleibt anrechenbar unverändert
  // (weil dort `anrechenbar=true` schon aktiv war).
  const confirmProof = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    onChange({
      ...row,
      _needs_proof: false,
      _review_verified: true,
      _source: "manual_confirmed",
      evidence: "strong",
      evidence_note: null,
      anrechenbar: row.evidence === "weak" ? true : row.anrechenbar,
    });
  };

  // Header-Chip-Label: kurze Beschreibung im kompakten Header.
  const headerLabel = row.label ||
    (manualMode ? "(Freitext-Ausbildung)" : "— Ausbildung wählen —");

  return (
    <li
      data-testid={`ausb-row-${index}`}
      data-evidence={row.evidence || undefined}
      data-needs-proof={needsProof ? "true" : "false"}
      data-review-verified={reviewVerified ? "true" : "false"}
      className={[
        "relative border rounded-sm bg-white",
        row.anrechenbar ? "border-ink-200" : "border-ink-200 bg-ink-50",
        // Sanfter Amber-Anstrich bei Prüfbedarf (ohne Vollflächen-Hintergrund
        // wie bei VordienstRow — konsistente Design-Sprache).
        showAmberSignal ? "border-amber-300" : "",
      ].filter(Boolean).join(" ")}
    >
      {/* Iter 20.36.f14.17 — 3-px amber Left-Border-Streifen für Prüf-
          relevante Zeilen (analog VordienstRow.f14.19). Verschwindet nach
          Nachweis-Bestätigung (`_review_verified=true`). */}
      {showAmberSignal && (
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-amber-400/80 rounded-l-sm"
          data-testid={`ausb-row-${index}-review-accent-bar`}
        />
      )}

      {/* ─── Kompakter Header (immer sichtbar) ───────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Label als Text-Chip (Klick öffnet Detail).
            Iter 20.36.f14.21 — Chips „KV-Sonder"/„Auto" entfernt (User-
            Feedback: visuell zu laut, VordienstRow hat auch keine solchen
            Chips). Provenance-Info steht im Detail-Panel unten (Begründung
            + Snippet). GraduationCap-Icon-Slot links entfernt — konsistent
            zur VordienstRow, die keinen Row-Icon-Slot hat. Der 3-px amber
            Left-Border-Streifen (oben) übernimmt weiterhin die visuelle
            Signalisierung „Prüfen". */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`ausb-row-${index}-header-label`}
          className="flex-1 min-w-0 text-left inline-flex items-center gap-2 truncate hover:text-brand-700 transition-crisp"
          title="Details ein-/ausklappen"
        >
          <span
            className={[
              "truncate text-sm font-medium",
              row.label ? "text-ink-900" : "text-warm-400 italic",
            ].join(" ")}
          >
            {headerLabel}
          </span>
        </button>

        {/* Duration-Chip (read-only für KV-Regeln) */}
        <span
          data-testid={`ausb-row-${index}-duration`}
          title={manualMode
            ? "Dauer manuell eingegeben (siehe Detail-Panel)"
            : "Diese Dauer ist im KV verbindlich vorgegeben."}
          className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-sm bg-ink-50 border border-ink-200 text-[12px] font-semibold tabular-nums text-ink-800"
        >
          {formatMonths(row.months)}
        </span>

        {/* Warn-Indikator — Toggle für `_review_verified` bei Prüf-
            bedürftigen Zeilen. Position + Semantik analog zum
            VordienstRow-`compact-warn-dot`: sitzt direkt VOR dem
            Anrechnen-Slider, Icon-Kreis 5×5, amber (nicht verifiziert) /
            emerald (verifiziert). Iter 20.36.f14.21 — Position von „ganz
            links" auf „vor Anrechnen-Slider" verschoben (User-Wunsch:
            selbe Position wie bei Vordienstzeiten).
            Iter 20.36.f14.22 — Hover-Popover mit Prüf-Hinweis (analog
            `vd-row-*-compact-warn-popover`). Erklärt in einem Satz, warum
            der Warn-Dot sichtbar ist und was der Sachbearbeiter tun soll. */}
        {(needsProof || weakEvidenceUnverified) && (
          <span className="relative inline-flex group shrink-0">
            <button
              type="button"
              onClick={confirmProof}
              aria-label={reviewVerified
                ? "Nachweis-Prüfung zurücksetzen"
                : "Nachweis-Prüfung abhaken"}
              aria-pressed={reviewVerified}
              data-testid={`ausb-row-${index}-review-toggle`}
              data-verified={reviewVerified ? "true" : "false"}
              className={[
                "shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full border transition-crisp focus:outline-none focus:ring-1",
                reviewVerified
                  ? "border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 focus:ring-emerald-500"
                  : "border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 focus:ring-amber-500",
              ].join(" ")}
            >
              {reviewVerified ? (
                <Check size={12} weight="bold" aria-hidden="true" />
              ) : (
                <Warning size={12} weight="fill" aria-hidden="true" />
              )}
            </button>
            <div
              role="tooltip"
              data-testid={`ausb-row-${index}-review-popover`}
              className="hidden group-hover:block group-focus-within:block absolute top-full right-0 mt-1 z-30 min-w-[240px] max-w-[340px] rounded-md border border-amber-300 bg-white shadow-lg px-3 py-2 pointer-events-none"
            >
              <div
                className={[
                  "text-[10px] font-bold uppercase tracking-wider mb-1 whitespace-nowrap",
                  reviewVerified ? "text-emerald-700" : "text-amber-800",
                ].join(" ")}
              >
                {reviewVerified ? "Nachweis bestätigt" : "Nachweis fehlt"}
              </div>
              <div className="text-[11.5px] leading-snug text-ink-700">
                {reviewVerified
                  ? "Der Sachbearbeiter hat den Nachweis manuell bestätigt. Klick zum Zurücksetzen."
                  : (row.evidence_note
                      || "Diese Ausbildung wurde nur durch Textauslese in den Dokumenten erkannt — ein Abschluss-Nachweis (Zeugnis, Prüfungsurkunde) ist nicht identifizierbar. Bitte prüfen und ggf. abhaken.")}
              </div>
            </div>
          </span>
        )}

        {/* Anrechnen-Slider (unchanged Design) */}
        <label
          className="shrink-0 inline-flex items-center gap-1.5 cursor-pointer select-none"
          title={row.anrechenbar
            ? "Diese Bonuszeit wird in die Anrechnung einbezogen"
            : "Diese Bonuszeit wird nicht angerechnet"}
        >
          <input
            type="checkbox"
            checked={row.anrechenbar}
            onChange={(e) => onChange({ ...row, anrechenbar: e.target.checked })}
            className="sr-only"
            data-testid={`ausb-row-${index}-anr`}
            aria-label={row.anrechenbar ? "Anrechnen deaktivieren" : "Anrechnen aktivieren"}
          />
          <span
            aria-hidden="true"
            className={[
              "relative inline-block h-4 w-7 rounded-full transition-colors",
              row.anrechenbar ? "bg-status-aktiv-border" : "bg-warm-300",
            ].join(" ")}
          >
            <span
              className={[
                "absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform",
                row.anrechenbar ? "translate-x-3.5" : "translate-x-0.5",
              ].join(" ")}
            />
          </span>
          <span
            className={[
              "text-[11px] font-medium whitespace-nowrap",
              row.anrechenbar ? "text-status-aktiv-text" : "text-warm-500",
            ].join(" ")}
          >
            Anrechnen
          </span>
        </label>

        {/* Chevron + Divider + Trash — Divider mit shrink-0 mx-1.5 h-4
            w-px bg-ink-200, identisch zu VordienstRow (Iter 20.03.P5-fix
            Runde 7: verhindert Miss-Klicks zwischen Chevron und Trash). */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Details ein-/ausklappen"
          aria-expanded={open}
          data-testid={`ausb-row-${index}-toggle`}
          className="shrink-0 text-warm-500 hover:text-ink-900 hover:bg-ink-100 rounded-sm p-1 transition-crisp"
        >
          {open
            ? <CaretUp size={14} weight="bold" />
            : <CaretDown size={14} weight="bold" />}
        </button>
        <span
          aria-hidden="true"
          className="shrink-0 mx-1.5 h-4 w-px bg-ink-200"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label="Zeile entfernen"
          data-testid={`ausb-row-${index}-remove`}
          className="shrink-0 text-warm-400 hover:text-status-abgelehnt-text hover:bg-rose-50 rounded-sm p-1 transition-crisp"
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>

      {/* ─── Expanded-Panel (Details) ────────────────────────────────────── */}
      {open && (
        <div
          className="px-3 pb-3 pt-1 border-t border-ink-100 space-y-3"
          data-testid={`ausb-row-${index}-detail`}
        >
          {/* Label-Select / Freitext-Input */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor={`ausb-${index}-label`}
              className="text-[10px] mb-0.5"
            >
              Ausbildung / Bonuszeit
            </Label>
            {manualMode ? (
              <input
                id={`ausb-${index}-label`}
                type="text"
                autoFocus
                value={row.label || ""}
                placeholder="z.B. Bundesheer 1995–1996"
                onChange={(e) =>
                  onChange({ ...row, label: e.target.value, ai_matched: false })
                }
                data-testid={`ausb-row-${index}-label-free`}
                className="w-full text-sm rounded-sm border border-ink-300 px-2 py-1.5 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
              />
            ) : (
              <select
                id={`ausb-${index}-label`}
                value={row.label}
                onChange={(e) => onSelectChange(e.target.value)}
                data-testid={`ausb-row-${index}-label`}
                className="w-full text-sm rounded-sm border border-ink-300 px-2 py-1.5 bg-white focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
              >
                <option value="">— Ausbildung wählen —</option>
                {ruleLabels.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={manualMode ? exitManualMode : enterManualMode}
                data-testid={`ausb-row-${index}-freitext-toggle`}
                className="text-[11px] text-warm-500 hover:text-brand-600 transition-crisp inline-flex items-center gap-1"
              >
                <PencilSimple size={11} weight="bold" />
                {manualMode
                  ? "Aus KV-Regeln wählen"
                  : "Sonstige Ausbildung (Freitext)"}
              </button>
              {/* Iter 20.36.f14.22 — Dezenter Hinweistext statt Amber-
                  Button. User-Wunsch: kein Second-Klick-Button im
                  expanded-Panel — der runde Warn-Badge im Header übernimmt
                  die Bestätigungs-Aktion samt Hover-Popover. Hier bleibt
                  nur ein neutraler Textmarker „Nachweis fehlt", damit im
                  ausgeklappten Modus klar ist, warum das Amber-Signal
                  greift. */}
              {(needsProof || weakEvidenceUnverified) && (
                <span
                  data-testid={`ausb-row-${index}-nachweis-hint`}
                  className="inline-flex items-center gap-1 text-[11px] text-amber-700"
                >
                  <Warning size={11} weight="fill" aria-hidden="true" />
                  Nachweis fehlt — bitte prüfen
                </span>
              )}
            </div>
          </div>

          {/* Duration (nur editierbar im Freitext-Modus) */}
          {manualMode && (
            <div className="flex flex-col gap-1.5">
              <Label
                htmlFor={`ausb-${index}-months`}
                className="text-[10px] mb-0.5"
              >
                Dauer (Monate)
              </Label>
              <div className="flex items-center gap-1.5">
                <input
                  id={`ausb-${index}-months`}
                  type="number"
                  min="0"
                  max="240"
                  value={row.months || 0}
                  onChange={(e) =>
                    onChange({ ...row, months: Math.max(0, Math.min(240, Number(e.target.value) || 0)) })
                  }
                  data-testid={`ausb-row-${index}-months`}
                  className="w-24 text-sm tabular-nums rounded-sm border border-ink-300 px-2 py-1.5 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                />
                <span className="text-xs text-warm-600">Mo.</span>
                {row.months > 0 && (
                  <span className="text-[11px] text-warm-500 tabular-nums ml-1">
                    = {formatMonths(row.months)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* KI-Snippet als Beleg-Ausschnitt */}
          {row.detected_snippet && (
            <div className="flex flex-col gap-0.5">
              <Label className="text-[10px] mb-0.5">
                Erkannt im Text
              </Label>
              <span
                data-testid={`ausb-row-${index}-detected-snippet`}
                className="text-[11px] text-warm-600 italic tracking-wide border-l-2 border-warm-300 pl-2 py-0.5"
              >
                &bdquo;{row.detected_snippet}&ldquo;
              </span>
            </div>
          )}

          {/* Begründung */}
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor={`ausb-${index}-begr`}
              className="text-[10px] mb-0.5"
            >
              Begründung
            </Label>
            <textarea
              id={`ausb-${index}-begr`}
              data-testid={`ausb-row-${index}-begruendung`}
              rows={3}
              value={row.begruendung || ""}
              onChange={(e) => onChange({ ...row, begruendung: e.target.value })}
              placeholder="z.B. HAK-Matura 2018 abgeschlossen — laut KV-Bonuszeit voll anrechenbar."
              className="w-full text-sm rounded-sm border border-ink-300 px-2 py-1.5 focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 resize-y"
            />
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * Step-3 "Ausbildungsbedingte Vordienstzeiten"-Sektion.
 *
 * Iter 20.36.f14.17 — Prüfen-Filter-Chip oberhalb der Liste. Ähnlich
 * zu VordienstBlock's `FilterChips`, aber nur ein einzelner Toggle-Chip
 * „X Ausbildungen prüfen" (statt 4-Filter-Leiste, weil die Ausbildungs-
 * liste in der Regel sehr kurz ist und die anderen Filter-Kategorien
 * kaum Mehrwert bringen).
 */
export default function AusbildungsBlock({
  regeln,
  rows,
  setRows,
}) {
  const hasRules = Array.isArray(regeln) && regeln.length > 0;
  const hasRows = rows.length > 0;

  // Iter 20.36.f14.17 — Prüfen-Filter-State (lokal, kein URL-Sync).
  const [showOnlyReview, setShowOnlyReview] = React.useState(false);

  // Iter 20.03 Runde 14 (Punkt A) — Bulk-Delete-Confirm.
  const [confirmClearAll, setConfirmClearAll] = React.useState(false);
  const cancelClearAllTimerRef = React.useRef(null);

  const requestClearAll = () => {
    setConfirmClearAll(true);
    if (cancelClearAllTimerRef.current) {
      clearTimeout(cancelClearAllTimerRef.current);
    }
    cancelClearAllTimerRef.current = setTimeout(() => {
      setConfirmClearAll(false);
    }, 6000);
  };

  const cancelClearAll = () => {
    setConfirmClearAll(false);
    if (cancelClearAllTimerRef.current) {
      clearTimeout(cancelClearAllTimerRef.current);
      cancelClearAllTimerRef.current = null;
    }
  };

  const confirmClearAllNow = () => {
    setRows([]);
    setConfirmClearAll(false);
    if (cancelClearAllTimerRef.current) {
      clearTimeout(cancelClearAllTimerRef.current);
      cancelClearAllTimerRef.current = null;
    }
  };

  React.useEffect(() => {
    return () => {
      if (cancelClearAllTimerRef.current) {
        clearTimeout(cancelClearAllTimerRef.current);
      }
    };
  }, []);

  const addRow = React.useCallback(() => {
    setRows([...rows, EmptyAusbildung()]);
  }, [rows, setRows]);

  const updateRow = (idx, next) => setRows(rows.map((r, i) => (i === idx ? next : r)));
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  // Iter 20.36.f14.17/19 — Count der prüfbedürftigen Zeilen:
  //   - `_needs_proof` (F14.16 Stufe-B-Marker — nach F14.18 nur noch für
  //     manuelle Bestätigung relevant)
  //   - `evidence === "weak"` (F14.19 Rückkehr zur 19.63.64-Vereinbarung
  //     bei Angestelltenlehrling-ohne-Zeugnis-Fällen aus Step-3-File)
  // beide zählen NICHT wenn Row bereits `_review_verified=true` ist.
  const reviewCount = React.useMemo(
    () => rows.filter((r) => {
      if (r?._review_verified === true) return false;
      return r?._needs_proof === true || r?.evidence === "weak";
    }).length,
    [rows],
  );

  // Iter 20.36.f14.17 — Wenn der User „nur Prüfen" aktiviert hat und
  // dann alle Prüfungen abhakt, kippt der Filter automatisch wieder aus
  // (sonst wäre die Liste leer und der User sähe „nichts").
  React.useEffect(() => {
    if (showOnlyReview && reviewCount === 0) {
      setShowOnlyReview(false);
    }
  }, [showOnlyReview, reviewCount]);

  const visibleIndices = React.useMemo(() => {
    if (!showOnlyReview) return rows.map((_, i) => i);
    return rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => {
        if (r?._review_verified === true) return false;
        return r?._needs_proof === true || r?.evidence === "weak";
      })
      .map(({ i }) => i);
  }, [rows, showOnlyReview]);

  if (!hasRules && !hasRows) return null;

  return (
    <div
      data-testid="tool-step3-ausb-block"
      className="border-t border-ink-200 pt-8 mt-2"
    >
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <GraduationCap
            size={16}
            weight="duotone"
            className="text-ink-500"
          />
          <Label className="mb-0">Ausbildungsbedingte Vordienstzeiten</Label>
        </div>
        <span className="block text-[11px] text-warm-500 mt-1 ml-6">
          Bonuszeiten lt. KV — werden zusätzlich auf die anrechenbare Dienstzeit gezählt.
        </span>
      </div>

      {hasRows ? (
        <>
          {/* Iter 20.36.f14.17 — Prüfen-Filter-Chip (nur sichtbar wenn
              tatsächlich Prüf-Bedarf besteht — verhindert leere Deko). */}
          {reviewCount > 0 && (
            <div
              className="mb-2 flex items-center gap-2"
              data-testid="tool-step3-ausb-filter-bar"
            >
              <button
                type="button"
                onClick={() => setShowOnlyReview((v) => !v)}
                data-testid="tool-step3-ausb-filter-review-toggle"
                data-active={showOnlyReview ? "true" : "false"}
                aria-pressed={showOnlyReview}
                className={[
                  "inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11.5px] font-medium transition-crisp cursor-pointer select-none",
                  showOnlyReview
                    ? "bg-amber-500 border-amber-500 text-white"
                    : "bg-amber-50 border-amber-400 text-amber-900 hover:bg-amber-100",
                ].join(" ")}
                title={showOnlyReview
                  ? "Alle Ausbildungen wieder anzeigen"
                  : "Nur Ausbildungen mit fehlendem Nachweis / Zeugnis anzeigen"}
              >
                <Warning size={12} weight="fill" />
                <span>Prüfen</span>
                <span
                  className={[
                    "inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[10px] font-semibold leading-none",
                    showOnlyReview
                      ? "bg-white text-amber-700"
                      : "bg-amber-500 text-white",
                  ].join(" ")}
                >
                  {reviewCount}
                </span>
              </button>
              {showOnlyReview && (
                <span className="text-[11px] text-warm-500">
                  Zeigt {visibleIndices.length} von {rows.length} Ausbildungen.
                </span>
              )}
            </div>
          )}

          <ul className="space-y-2" data-testid="tool-step3-ausb-rows">
            {visibleIndices.map((i) => (
              <AusbildungRow
                key={i}
                row={rows[i]}
                regeln={regeln}
                index={i}
                onChange={(next) => updateRow(i, next)}
                onRemove={() => removeRow(i)}
              />
            ))}
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={addRow}
              data-testid="tool-step3-ausb-add"
              className="inline-flex items-center justify-center gap-2 border border-dashed border-ink-300 hover:border-brand-400 bg-white hover:bg-brand-50/40 text-ink-600 hover:text-brand-700 rounded-sm py-2 px-4 text-[12px] font-medium transition-crisp"
              title="Weitere Ausbildungszeit anlegen"
            >
              <Plus size={13} weight="bold" />
              Weitere Ausbildung hinzufügen
            </button>
            {!confirmClearAll ? (
              <button
                type="button"
                onClick={requestClearAll}
                data-testid="tool-step3-ausb-clear"
                className="inline-flex items-center gap-1.5 text-[12px] text-warm-500 hover:text-status-abgelehnt-text transition-crisp px-2 py-1.5"
                title="Alle Ausbildungszeiten entfernen"
              >
                <Trash size={12} weight="bold" />
                Alle entfernen
              </button>
            ) : (
              <div
                data-testid="tool-step3-ausb-clear-confirm-bar"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-status-abgelehnt-border/60 bg-status-abgelehnt-bg/30 rounded-sm"
              >
                <Warning
                  size={14}
                  weight="fill"
                  className="text-status-abgelehnt-text shrink-0"
                />
                <span className="text-[11.5px] text-status-abgelehnt-text font-medium">
                  Wirklich {rows.length}&nbsp;{rows.length === 1 ? "Zeile" : "Zeilen"}&nbsp;entfernen?
                </span>
                <button
                  type="button"
                  onClick={confirmClearAllNow}
                  data-testid="tool-step3-ausb-clear-confirm"
                  className="inline-flex items-center gap-1 border border-status-abgelehnt-border bg-status-abgelehnt-bg text-status-abgelehnt-text hover:bg-status-abgelehnt-border/40 rounded-sm px-2 py-1 text-[11.5px] font-semibold transition-crisp"
                >
                  <Trash size={11} weight="bold" />
                  Ja, entfernen
                </button>
                <button
                  type="button"
                  onClick={cancelClearAll}
                  data-testid="tool-step3-ausb-clear-cancel"
                  className="text-[11.5px] text-warm-600 hover:text-ink-900 transition-crisp px-2 py-1"
                >
                  Abbrechen
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div
          data-testid="tool-step3-ausb-empty"
          className="border border-dashed border-warm-300 bg-warm-50 rounded-sm px-4 py-5 text-center"
        >
          <p className="text-sm text-warm-600 mb-3">
            Noch keine Ausbildungszeiten erfasst.
            <br />
            <span className="text-[11px]">
              (HAK / Lehre / sonstige Bonuszeiten lt. KV)
            </span>
          </p>
          <button
            type="button"
            onClick={addRow}
            data-testid="tool-step3-ausb-expand"
            className="inline-flex items-center justify-center gap-2 border border-dashed border-ink-300 hover:border-brand-400 bg-white hover:bg-brand-50/40 text-ink-600 hover:text-brand-700 rounded-sm py-2 px-4 text-[12px] font-medium transition-crisp"
            title="Ausbildungszeit anlegen"
          >
            <Plus size={13} weight="bold" />
            Ausbildungszeit hinzufügen
          </button>
        </div>
      )}
    </div>
  );
}
